import { Button, Flex, Modal, Transfer } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFusion, useJourney } from 'services/journeys';
import { useJourneyDashboardContext } from '../context';
import { usePrepRoutes } from 'services/journeys/list';
import { TransferItem } from 'antd/lib/transfer';
import { Journey } from 'interfaces/journey';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  ImportOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';

interface FuseRoutesButtonProps {
  journeyId?: string;
  disabled?: boolean;
  mode: 'list' | 'dashboard';
}

interface RouteTransferProps {
  journeyId: string;
  selectedRoutes: string[];
  setSelectedRoutes: React.Dispatch<React.SetStateAction<string[]>>;
  successJourneys?: string[];
  failedJourneys?: string[];
  disabled?: boolean;
  loading?: boolean;
}

const RouteTransfer = ({
  journeyId,
  selectedRoutes,
  setSelectedRoutes,
  successJourneys = [],
  failedJourneys = [],
  loading,
  disabled,
}: RouteTransferProps) => {
  const { data: journeyData } = useJourney(journeyId);
  const packagesAmount = useRef<number>(journeyData?.totalPackages || 0);
  const { data: draftJourneys } = usePrepRoutes();

  const dataSource: (TransferItem & Journey)[] = useMemo(
    () =>
      draftJourneys &&
      draftJourneys.journeys &&
      draftJourneys.journeys?.length > 0
        ? draftJourneys.journeys
            .filter(
              (journey: Journey) =>
                journey.journeyId !== journeyId &&
                journey.totalPackages &&
                journey.totalPackages > 0,
            )
            .map((journey: Journey) => ({
              key: journey.journeyId,
              title: journey.journeyId,
              ...journey,
            }))
        : [],
    [draftJourneys],
  );

  return (
    <Flex style={{ margin: '16px 0' }}>
      <Transfer
        dataSource={dataSource}
        targetKeys={selectedRoutes}
        style={{ width: '100%' }}
        showSelectAll={false}
        disabled={disabled}
        listStyle={{
          width: '100%',
          height: 300,
        }}
        onChange={(nextTargetKeys) =>
          setSelectedRoutes(nextTargetKeys.map(String))
        }
        oneWay
        render={(item) => {
          const isSuccess = successJourneys.includes(item.journeyId || '');
          const isFailure = failedJourneys.includes(item.journeyId || '');
          return (
            <Flex justify="space-between">
              <Flex gap="8px">
                {isSuccess ? (
                  <CheckOutlined style={{ color: 'green' }} />
                ) : isFailure ? (
                  <CloseOutlined style={{ color: 'red' }} />
                ) : null}
                <p
                  style={{
                    color: isSuccess ? 'green' : isFailure ? 'red' : 'black',
                  }}
                >
                  {item.title}
                </p>
              </Flex>
              <p>{item.totalPackages}</p>
            </Flex>
          );
        }}
        titles={['#', journeyId]}
        footer={(_, info) => {
          if (info?.direction === 'left') {
            return;
          }
          const calculateTotalPackages = (routeIds: string[]) => {
            return routeIds.reduce((sum, routeId) => {
              const route = dataSource.find((item) => item.key === routeId);
              return sum + (route?.totalPackages || 0);
            }, 0);
          };

          const isFusionCompleted =
            successJourneys.length > 0 || failedJourneys.length > 0;
          const totalPackagesCalculated = calculateTotalPackages(
            isFusionCompleted ? successJourneys : selectedRoutes,
          );

          return (
            <Flex gap={'8px'} justify="end" style={{ margin: '8px' }}>
              {loading && <LoadingOutlined />}
              <p style={isFusionCompleted ? { fontWeight: 'bold' } : {}}>
                {totalPackagesCalculated + (packagesAmount.current || 0)}{' '}
                paquete/s total/es
              </p>
            </Flex>
          );
        }}
      />
    </Flex>
  );
};

const FuseRoutesButton = React.forwardRef<
  HTMLButtonElement,
  FuseRoutesButtonProps
>(({ disabled, mode, journeyId }, ref) => {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const [selectedRoutes, setSelectedRoutes] = useState<string[]>([]);
  const [successJourneys, setSuccessJourneys] = useState<string[]>([]);
  const [failedJourneys, setFailedJourneys] = useState<string[]>([]);
  const { mutateAsync: fuseRoutes, isPending } = useFusion();

  if (!journeyId) {
    return null;
  }

  return (
    <>
      <Modal
        destroyOnHidden
        open={open}
        onOk={() => {
          if (journeyId) {
            if (successJourneys.length > 0 || failedJourneys.length > 0) {
              queryClient.invalidateQueries({
                queryKey: ['journey', journeyId],
              });
              queryClient.invalidateQueries({
                queryKey: ['prepRoutes'],
              });
              queryClient.invalidateQueries({
                queryKey: ['trackingRoutes'],
              });
              setSelectedRoutes([]);
              setSuccessJourneys([]);
              setFailedJourneys([]);
              setOpen(false);
              return;
            }
            fuseRoutes({ journeyId, journeys: selectedRoutes }).then(
              (response) => {
                if (response) {
                  setSuccessJourneys(
                    response.successful.map((j) => j.journeyId) || [],
                  );
                  setFailedJourneys(
                    response.failed.map((j) => j.journeyId) || [],
                  );
                }
              },
            );
          }
        }}
        onCancel={() => setOpen(false)}
        title="Unión de rutas"
        okText={
          successJourneys.length > 0 || failedJourneys.length > 0
            ? 'Finalizar'
            : 'Unir'
        }
        cancelText="Cancelar"
        okButtonProps={{ disabled: selectedRoutes.length === 0 || isPending }}
        confirmLoading={isPending}
      >
        Seleccione las rutas que desea unir a la ruta{' '}
        {mode === 'dashboard' ? 'actual' : 'seleccionada'}.
        <RouteTransfer
          journeyId={journeyId}
          disabled={successJourneys.length > 0 || failedJourneys.length > 0}
          selectedRoutes={selectedRoutes}
          setSelectedRoutes={setSelectedRoutes}
          successJourneys={successJourneys}
          failedJourneys={failedJourneys}
          loading={isPending}
        />
      </Modal>
      <Button
        ref={ref}
        type={mode === 'dashboard' ? 'default' : 'text'}
        size={'small'}
        icon={<ImportOutlined />}
        disabled={disabled || isPending}
        onClick={() => setOpen(true)}
      >
        Unir {mode === 'dashboard' ? 'rutas' : ''}
      </Button>
    </>
  );
});

export default FuseRoutesButton;
